import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, X, Loader2, Upload, ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Import all article images
import articleEtudeCas from '@/assets/article-etude-cas-conseillere.jpg';
import articleAgenceMarketing from '@/assets/article-agence-marketing-b2b.jpg';
import articleMarketingVideo from '@/assets/article-marketing-video-b2b.jpg';
import articleMarketingContenu from '@/assets/article-marketing-contenu-b2b.jpg';
import articleCreationContenu from '@/assets/article-creation-contenu-b2b.jpg';
import articleAgenceCreation from '@/assets/article-agence-creation-contenu.jpg';
import articleAccompagnement from '@/assets/article-accompagnement-marketing.jpg';
import articleContenuPasClients from '@/assets/article-contenu-pas-clients.jpg';
import articleOrganiqueVsPayant from '@/assets/article-organique-vs-payant.jpg';
import articleMarketingQuebec from '@/assets/article-marketing-contenu-quebec.jpg';
import articleContenuVsPub from '@/assets/article-contenu-vs-pub.jpg';
import articleErreurs from '@/assets/article-erreurs-marketing.jpg';
import articleGenerationLeads from '@/assets/article-generation-leads.jpg';
import articleStrategie from '@/assets/article-strategie-contenu.jpg';
import articleTempsLeads from '@/assets/article-temps-leads.jpg';
import articleAgenceMontreal from '@/assets/article-agence-montreal.jpg';
import articleClientIdeal from '@/assets/article-client-ideal.jpg';
import articleMachineLeads from '@/assets/article-machine-leads.jpg';
import articleSimplifier from '@/assets/article-simplifier-offre.jpg';
import articleReels from '@/assets/article-reels.jpg';
import articlePodcast from '@/assets/article-podcast.jpg';
import articleEvent from '@/assets/article-event.jpg';
import articleFormation from '@/assets/article-formation.jpg';
import articleMetaAds from '@/assets/article-meta-ads.jpg';
import articlePersonalBranding from '@/assets/article-personal-branding.jpg';
import articleVuesVsResultats from '@/assets/article-vues-vs-resultats.jpg';

interface ImageStatus {
  name: string;
  src: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  url?: string;
  error?: string;
}

const ARTICLE_IMAGES: { name: string; src: string }[] = [
  { name: 'article-etude-cas-conseillere.jpg', src: articleEtudeCas },
  { name: 'article-agence-marketing-b2b.jpg', src: articleAgenceMarketing },
  { name: 'article-marketing-video-b2b.jpg', src: articleMarketingVideo },
  { name: 'article-marketing-contenu-b2b.jpg', src: articleMarketingContenu },
  { name: 'article-creation-contenu-b2b.jpg', src: articleCreationContenu },
  { name: 'article-agence-creation-contenu.jpg', src: articleAgenceCreation },
  { name: 'article-accompagnement-marketing.jpg', src: articleAccompagnement },
  { name: 'article-contenu-pas-clients.jpg', src: articleContenuPasClients },
  { name: 'article-organique-vs-payant.jpg', src: articleOrganiqueVsPayant },
  { name: 'article-marketing-contenu-quebec.jpg', src: articleMarketingQuebec },
  { name: 'article-contenu-vs-pub.jpg', src: articleContenuVsPub },
  { name: 'article-erreurs-marketing.jpg', src: articleErreurs },
  { name: 'article-generation-leads.jpg', src: articleGenerationLeads },
  { name: 'article-strategie-contenu.jpg', src: articleStrategie },
  { name: 'article-temps-leads.jpg', src: articleTempsLeads },
  { name: 'article-agence-montreal.jpg', src: articleAgenceMontreal },
  { name: 'article-client-ideal.jpg', src: articleClientIdeal },
  { name: 'article-machine-leads.jpg', src: articleMachineLeads },
  { name: 'article-simplifier-offre.jpg', src: articleSimplifier },
  { name: 'article-reels.jpg', src: articleReels },
  { name: 'article-podcast.jpg', src: articlePodcast },
  { name: 'article-event.jpg', src: articleEvent },
  { name: 'article-formation.jpg', src: articleFormation },
  { name: 'article-meta-ads.jpg', src: articleMetaAds },
  { name: 'article-personal-branding.jpg', src: articlePersonalBranding },
  { name: 'article-vues-vs-resultats.jpg', src: articleVuesVsResultats },
];

const MigrateImages = () => {
  const [images, setImages] = useState<ImageStatus[]>(
    ARTICLE_IMAGES.map(img => ({ ...img, status: 'pending' as const }))
  );
  const [isRunning, setIsRunning] = useState(false);

  const imageToBase64 = async (src: string): Promise<string> => {
    const response = await fetch(src);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Remove the data URL prefix to get just the base64 data
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const getContentType = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'webp':
        return 'image/webp';
      case 'gif':
        return 'image/gif';
      default:
        return 'image/jpeg';
    }
  };

  const uploadImage = async (image: ImageStatus, index: number) => {
    setImages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], status: 'uploading' };
      return updated;
    });

    try {
      const base64Data = await imageToBase64(image.src);
      const contentType = getContentType(image.name);

      const { data, error } = await supabase.functions.invoke('upload-article-image', {
        body: {
          filename: image.name,
          base64Data,
          contentType,
        },
      });

      if (error) throw error;

      setImages(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], status: 'success', url: data.url };
        return updated;
      });

      return { success: true, url: data.url };
    } catch (error: any) {
      console.error(`Error uploading ${image.name}:`, error);
      setImages(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], status: 'error', error: error.message };
        return updated;
      });
      return { success: false, error: error.message };
    }
  };

  const runMigration = async () => {
    setIsRunning(true);
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < images.length; i++) {
      const result = await uploadImage(images[i], i);
      if (result.success) {
        successCount++;
      } else {
        errorCount++;
      }
      // Small delay between uploads to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    setIsRunning(false);

    if (errorCount === 0) {
      toast.success(`Migration terminée! ${successCount} images uploadées avec succès.`);
    } else {
      toast.warning(`Migration terminée avec ${errorCount} erreur(s). ${successCount} images uploadées.`);
    }
  };

  const completedCount = images.filter(img => img.status === 'success').length;
  const errorCount = images.filter(img => img.status === 'error').length;
  const progress = (completedCount / images.length) * 100;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Migration des Images d'Articles
          </CardTitle>
          <CardDescription>
            Uploadez les 26 images d'articles vers le stockage cloud pour corriger les liens brisés.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium">
                Progression: {completedCount}/{images.length} images
              </p>
              {errorCount > 0 && (
                <p className="text-sm text-destructive">{errorCount} erreur(s)</p>
              )}
            </div>
            <Button 
              onClick={runMigration} 
              disabled={isRunning || completedCount === images.length}
              size="lg"
            >
              {isRunning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Migration en cours...
                </>
              ) : completedCount === images.length ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Migration terminée
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Migrer toutes les images
                </>
              )}
            </Button>
          </div>

          <Progress value={progress} className="h-3" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
            {images.map((image, index) => (
              <div
                key={image.name}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  image.status === 'success'
                    ? 'border-green-500/30 bg-green-500/5'
                    : image.status === 'error'
                    ? 'border-destructive/30 bg-destructive/5'
                    : image.status === 'uploading'
                    ? 'border-primary/30 bg-primary/5'
                    : 'border-border'
                }`}
              >
                <div className="relative w-12 h-12 rounded overflow-hidden bg-muted flex-shrink-0">
                  <img
                    src={image.src}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{image.name}</p>
                  {image.error && (
                    <p className="text-xs text-destructive truncate">{image.error}</p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {image.status === 'pending' && (
                    <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                  )}
                  {image.status === 'uploading' && (
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  )}
                  {image.status === 'success' && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                  {image.status === 'error' && (
                    <X className="w-5 h-5 text-destructive" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MigrateImages;
